<template>
  <div
    class="min-h-screen bg-[#F5F5F7] dark:bg-black font-sans text-[#1d1d1f] dark:text-[#f5f5f7] p-4 sm:p-6 lg:p-8 flex flex-col items-center">

    <header class="w-full max-w-[1200px] mb-8 flex flex-col items-center text-center">
      <h1 class="text-[32px] font-bold tracking-tight leading-none text-[#1d1d1f] dark:text-white mb-2">
        Calculadora <span class="text-transparent bg-clip-text bg-gradient-to-r from-[#7B2CBF] to-[#9D4EDD]">Ease</span>
      </h1>
      <p class="text-[15px] text-[#86868b]">Ferramentas precisas para suas decisões financeiras.</p>
    </header>

    <div class="bg-[#e3e3e8] dark:bg-[#1c1c1e] p-1 rounded-[12px] flex items-center mb-8 shadow-inner w-full max-w-xs">
      <button @click="currentMode = 'financial'"
        class="flex-1 py-1.5 px-3 rounded-[9px] text-[13px] font-semibold transition-all duration-300 flex items-center justify-center gap-2"
        :class="currentMode === 'financial' ? 'bg-white dark:bg-[#636366] text-black dark:text-white shadow-sm' : 'text-[#86868b] hover:text-black dark:hover:text-white'">
        <span class="material-symbols-outlined text-[18px]">savings</span>
        Financeira
      </button>
      <button @click="currentMode = 'standard'"
        class="flex-1 py-1.5 px-3 rounded-[9px] text-[13px] font-semibold transition-all duration-300 flex items-center justify-center gap-2"
        :class="currentMode === 'standard' ? 'bg-white dark:bg-[#636366] text-black dark:text-white shadow-sm' : 'text-[#86868b] hover:text-black dark:hover:text-white'">
        <span class="material-symbols-outlined text-[18px]">dialpad</span>
        Padrão
      </button>
    </div>

    <transition name="fade" mode="out-in">

      <div v-if="currentMode === 'standard'" key="standard" class="w-full max-w-[360px]">
        <div
          class="bg-white dark:bg-[#1c1c1e] rounded-[32px] p-6 shadow-2xl shadow-black/5 dark:shadow-none border border-black/5 dark:border-white/10">
          <div class="mb-6 text-right px-2">
            <div class="text-[#86868b] text-[15px] h-6 font-medium mb-1 truncate">{{ expressionDisplay }}</div>
            <div
              class="text-[48px] leading-none font-light text-[#1d1d1f] dark:text-white break-all tabular-nums tracking-tight">
              {{ displayValue }}</div>
          </div>

          <div class="grid grid-cols-4 gap-3">
            <button @click="clear"
              class="h-16 w-16 rounded-full bg-[#e5e5ea] dark:bg-[#3a3a3c] text-[#1d1d1f] dark:text-white text-[19px] font-medium hover:brightness-95 active:scale-95 transition-all">C</button>
            <button @click="toggleSign"
              class="h-16 w-16 rounded-full bg-[#e5e5ea] dark:bg-[#3a3a3c] text-[#1d1d1f] dark:text-white text-[19px] font-medium hover:brightness-95 active:scale-95 transition-all">+/-</button>
            <button @click="handlePercentage"
              class="h-16 w-16 rounded-full bg-[#e5e5ea] dark:bg-[#3a3a3c] text-[#1d1d1f] dark:text-white text-[19px] font-medium hover:brightness-95 active:scale-95 transition-all">%</button>
            <button @click="handleOperator('/')"
              class="h-16 w-16 rounded-full bg-[#FF9500] text-white text-[24px] font-medium hover:brightness-110 active:scale-95 transition-all">÷</button>

            <button @click="inputDigit('7')"
              class="h-16 w-16 rounded-full bg-[#f5f5f7] dark:bg-[#2c2c2e] text-[#1d1d1f] dark:text-white text-[24px] font-medium hover:brightness-95 active:scale-95 transition-all">7</button>
            <button @click="inputDigit('8')"
              class="h-16 w-16 rounded-full bg-[#f5f5f7] dark:bg-[#2c2c2e] text-[#1d1d1f] dark:text-white text-[24px] font-medium hover:brightness-95 active:scale-95 transition-all">8</button>
            <button @click="inputDigit('9')"
              class="h-16 w-16 rounded-full bg-[#f5f5f7] dark:bg-[#2c2c2e] text-[#1d1d1f] dark:text-white text-[24px] font-medium hover:brightness-95 active:scale-95 transition-all">9</button>
            <button @click="handleOperator('*')"
              class="h-16 w-16 rounded-full bg-[#FF9500] text-white text-[24px] font-medium hover:brightness-110 active:scale-95 transition-all">×</button>

            <button @click="inputDigit('4')"
              class="h-16 w-16 rounded-full bg-[#f5f5f7] dark:bg-[#2c2c2e] text-[#1d1d1f] dark:text-white text-[24px] font-medium hover:brightness-95 active:scale-95 transition-all">4</button>
            <button @click="inputDigit('5')"
              class="h-16 w-16 rounded-full bg-[#f5f5f7] dark:bg-[#2c2c2e] text-[#1d1d1f] dark:text-white text-[24px] font-medium hover:brightness-95 active:scale-95 transition-all">5</button>
            <button @click="inputDigit('6')"
              class="h-16 w-16 rounded-full bg-[#f5f5f7] dark:bg-[#2c2c2e] text-[#1d1d1f] dark:text-white text-[24px] font-medium hover:brightness-95 active:scale-95 transition-all">6</button>
            <button @click="handleOperator('-')"
              class="h-16 w-16 rounded-full bg-[#FF9500] text-white text-[24px] font-medium hover:brightness-110 active:scale-95 transition-all">−</button>

            <button @click="inputDigit('1')"
              class="h-16 w-16 rounded-full bg-[#f5f5f7] dark:bg-[#2c2c2e] text-[#1d1d1f] dark:text-white text-[24px] font-medium hover:brightness-95 active:scale-95 transition-all">1</button>
            <button @click="inputDigit('2')"
              class="h-16 w-16 rounded-full bg-[#f5f5f7] dark:bg-[#2c2c2e] text-[#1d1d1f] dark:text-white text-[24px] font-medium hover:brightness-95 active:scale-95 transition-all">2</button>
            <button @click="inputDigit('3')"
              class="h-16 w-16 rounded-full bg-[#f5f5f7] dark:bg-[#2c2c2e] text-[#1d1d1f] dark:text-white text-[24px] font-medium hover:brightness-95 active:scale-95 transition-all">3</button>
            <button @click="handleOperator('+')"
              class="h-16 w-16 rounded-full bg-[#FF9500] text-white text-[24px] font-medium hover:brightness-110 active:scale-95 transition-all">+</button>

            <button @click="inputDigit('0')"
              class="h-16 rounded-full bg-[#f5f5f7] dark:bg-[#2c2c2e] text-[#1d1d1f] dark:text-white text-[24px] font-medium hover:brightness-95 active:scale-95 transition-all col-span-2 text-left pl-7">0</button>
            <button @click="inputDecimal"
              class="h-16 w-16 rounded-full bg-[#f5f5f7] dark:bg-[#2c2c2e] text-[#1d1d1f] dark:text-white text-[24px] font-medium hover:brightness-95 active:scale-95 transition-all">.</button>
            <button @click="calculate"
              class="h-16 w-16 rounded-full bg-[#FF9500] text-white text-[24px] font-medium hover:brightness-110 active:scale-95 transition-all">=</button>
          </div>
        </div>
      </div>

      <div v-else key="financial" class="w-full max-w-[1200px] grid grid-cols-1 md:grid-cols-[260px_1fr] gap-6">

        <aside class="space-y-4">
          <div
            class="bg-white dark:bg-[#1c1c1e] rounded-[20px] p-2 shadow-sm border border-black/5 dark:border-white/10 md:sticky md:top-6 overflow-x-auto md:overflow-visible flex md:block custom-scroll">
            <button v-for="calc in financialCalculators" :key="calc.id" @click="activeFinancialCalc = calc.id"
              class="flex-shrink-0 md:w-full flex items-center gap-3 px-4 py-3 rounded-[14px] text-[14px] font-medium transition-all"
              :class="activeFinancialCalc === calc.id ? 'bg-[#7B2CBF] text-white shadow-md' : 'text-[#1d1d1f] dark:text-[#f5f5f7] hover:bg-[#F5F5F7] dark:hover:bg-[#2c2c2e]'">
              <span class="material-symbols-outlined text-[20px]">{{ calc.icon }}</span>
              <span class="whitespace-nowrap">{{ calc.name }}</span>
            </button>
          </div>
        </aside>

        <main
          class="bg-white dark:bg-[#1c1c1e] rounded-[24px] p-6 sm:p-8 shadow-sm border border-black/5 dark:border-white/10 min-h-[500px]">
          <transition name="fade-fast" mode="out-in">
            <div :key="activeFinancialCalc" class="max-w-2xl mx-auto">

              <!-- JUROS COMPOSTOS -->
              <div v-if="activeFinancialCalc === 'compoundInterest'">
                <h2 class="text-[22px] font-bold mb-6 text-[#1d1d1f] dark:text-white">Juros Compostos</h2>
                <div class="grid gap-5">
                  <div class="input-group">
                    <label>Valor Inicial (R$)</label>
                    <input v-model.number="ci.initial" type="number" placeholder="1000.00" />
                  </div>
                  <div class="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div class="input-group">
                      <label>Aporte Mensal (R$)</label>
                      <input v-model.number="ci.monthly" type="number" placeholder="200.00" />
                    </div>
                    <div class="input-group">
                      <label>Taxa Anual (%)</label>
                      <input v-model.number="ci.rate" type="number" placeholder="10" />
                    </div>
                  </div>
                  <div class="input-group">
                    <label>Período (anos)</label>
                    <input v-model.number="ci.years" type="number" placeholder="5" />
                  </div>
                  <button @click="calculateCompoundInterest" class="btn-action mt-2">Calcular Rendimento</button>

                  <div v-if="ci.result" class="result-card mt-6">
                    <div class="text-center p-4 border-b border-black/5 dark:border-white/10">
                      <p class="text-[13px] uppercase tracking-wide font-semibold text-[#86868b] mb-1">Valor Total
                        Acumulado</p>
                      <p class="text-[32px] font-bold text-[#7B2CBF]">{{ formatBRL(ci.result.futureValue) }}</p>
                    </div>
                    <div class="grid grid-cols-2 divide-x divide-black/5 dark:divide-white/10">
                      <div class="p-4 text-center">
                        <p class="text-[12px] text-[#86868b] mb-1">Total Investido</p>
                        <p class="font-semibold text-[#1d1d1f] dark:text-white">{{ formatBRL(ci.result.totalInvested) }}
                        </p>
                      </div>
                      <div class="p-4 text-center">
                        <p class="text-[12px] text-[#86868b] mb-1">Total em Juros</p>
                        <p class="font-semibold text-[#34C759]">+{{ formatBRL(ci.result.totalInterest) }}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- FINANCIAMENTO -->
              <div v-else-if="activeFinancialCalc === 'loanPayment'">
                <h2 class="text-[22px] font-bold mb-6 text-[#1d1d1f] dark:text-white">Financiamento</h2>
                <div class="grid gap-5">
                  <div class="input-group">
                    <label>Valor do Empréstimo (R$)</label>
                    <input v-model.number="loan.amount" type="number" placeholder="50000.00" />
                  </div>
                  <div class="grid grid-cols-2 gap-5">
                    <div class="input-group">
                      <label>Taxa Anual (%)</label>
                      <input v-model.number="loan.rate" type="number" placeholder="12" />
                    </div>
                    <div class="input-group">
                      <label>Anos</label>
                      <input v-model.number="loan.years" type="number" placeholder="10" />
                    </div>
                  </div>
                  <button @click="calculateLoanPayment" class="btn-action mt-2">Simular Parcela</button>

                  <div v-if="loan.result" class="result-card mt-6">
                    <div class="text-center p-4 border-b border-black/5 dark:border-white/10">
                      <p class="text-[13px] uppercase tracking-wide font-semibold text-[#86868b] mb-1">Parcela Mensal
                      </p>
                      <p class="text-[32px] font-bold text-[#FF3B30]">{{ formatBRL(loan.result.monthlyPayment) }}</p>
                    </div>
                    <div class="grid grid-cols-2 divide-x divide-black/5 dark:divide-white/10">
                      <div class="p-4 text-center">
                        <p class="text-[12px] text-[#86868b] mb-1">Custo Total</p>
                        <p class="font-semibold text-[#1d1d1f] dark:text-white">{{ formatBRL(loan.result.totalCost) }}
                        </p>
                      </div>
                      <div class="p-4 text-center">
                        <p class="text-[12px] text-[#86868b] mb-1">Total Juros</p>
                        <p class="font-semibold text-[#FF3B30]">{{ formatBRL(loan.result.totalInterest) }}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- INFLAÇÃO -->
              <div v-else-if="activeFinancialCalc === 'inflation'">
                <h2 class="text-[22px] font-bold mb-6 text-[#1d1d1f] dark:text-white">Calculadora de Inflação</h2>
                <div class="grid gap-5">
                  <div class="input-group">
                    <label>Valor Atual (R$)</label>
                    <input v-model.number="inflation.currentValue" type="number" placeholder="1000.00" />
                  </div>
                  <div class="grid grid-cols-2 gap-5">
                    <div class="input-group">
                      <label>Taxa Anual (%)</label>
                      <input v-model.number="inflation.rate" type="number" placeholder="5" />
                    </div>
                    <div class="input-group">
                      <label>Anos</label>
                      <input v-model.number="inflation.years" type="number" placeholder="10" />
                    </div>
                  </div>
                  <button @click="calculateInflation" class="btn-action mt-2">Calcular Perda</button>

                  <div v-if="inflation.result" class="result-card mt-6 p-6 text-center">
                    <p class="text-[13px] uppercase tracking-wide font-semibold text-[#86868b] mb-2">Poder de Compra
                      Futuro</p>
                    <p class="text-[36px] font-bold text-[#FF9500]">{{ formatBRL(inflation.result.futureValue) }}</p>
                    <p class="text-[13px] text-[#86868b] mt-2">Equivalente daqui a {{ inflation.years }} anos</p>
                  </div>
                </div>
              </div>

              <!-- APOSENTADORIA -->
              <div v-else-if="activeFinancialCalc === 'retirement'">
                <h2 class="text-[22px] font-bold mb-6 text-[#1d1d1f] dark:text-white">Planejamento Aposentadoria</h2>
                <div class="grid gap-5">
                  <div class="grid grid-cols-2 gap-5">
                    <div class="input-group">
                      <label>Idade Atual</label>
                      <input v-model.number="retirement.currentAge" type="number" placeholder="30" />
                    </div>
                    <div class="input-group">
                      <label>Idade Aposentar</label>
                      <input v-model.number="retirement.retirementAge" type="number" placeholder="65" />
                    </div>
                  </div>
                  <div class="input-group">
                    <label>Renda Mensal Desejada (R$)</label>
                    <input v-model.number="retirement.monthlyIncome" type="number" placeholder="5000" />
                  </div>
                  <div class="input-group">
                    <label>Rendimento Anual Esperado (%)</label>
                    <input v-model.number="retirement.returnRate" type="number" placeholder="8" />
                  </div>
                  <button @click="calculateRetirement" class="btn-action mt-2">Calcular Meta</button>

                  <div v-if="retirement.result" class="result-card mt-6">
                    <div
                      class="grid grid-cols-1 sm:grid-cols-2 divide-y sm:divide-y-0 sm:divide-x divide-black/5 dark:divide-white/10">
                      <div class="p-5 text-center">
                        <p class="text-[12px] text-[#86868b] mb-1 uppercase font-bold">Patrimônio Necessário</p>
                        <p class="text-[24px] font-bold text-[#7B2CBF]">{{ formatBRL(retirement.result.totalNeeded) }}
                        </p>
                      </div>
                      <div class="p-5 text-center">
                        <p class="text-[12px] text-[#86868b] mb-1 uppercase font-bold">Investimento Mensal</p>
                        <p class="text-[24px] font-bold text-[#34C759]">{{
                          formatBRL(retirement.result.monthlyInvestment) }}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- RENDIMENTO CDB/LC -->
              <div v-else-if="activeFinancialCalc === 'investmentYield'">
                <h2 class="text-[22px] font-bold mb-6 text-[#1d1d1f] dark:text-white">Rendimento CDB/LC</h2>
                <div class="grid gap-5">
                  <div class="input-group">
                    <label>Valor Aplicado (R$)</label>
                    <input v-model.number="investment.initial" type="number" placeholder="10000" />
                  </div>
                  <div class="grid grid-cols-2 gap-5">
                    <div class="input-group">
                      <label>Taxa Anual (%)</label>
                      <input v-model.number="investment.rate" type="number" placeholder="12" />
                    </div>
                    <div class="input-group">
                      <label>Dias Úteis</label>
                      <input v-model.number="investment.days" type="number" placeholder="730" />
                    </div>
                  </div>
                  <button @click="calculateInvestmentYield" class="btn-action mt-2">Calcular Líquido</button>

                  <div v-if="investment.result" class="result-card mt-6 p-5 space-y-3">
                    <div class="flex justify-between items-center text-[14px]">
                      <span class="text-[#86868b]">Valor Bruto</span>
                      <span class="font-medium">{{ formatBRL(investment.result.grossValue) }}</span>
                    </div>
                    <div class="flex justify-between items-center text-[14px]">
                      <span class="text-[#86868b]">Rendimento</span>
                      <span class="font-medium text-[#34C759]">+ {{ formatBRL(investment.result.grossProfit) }}</span>
                    </div>
                    <div class="flex justify-between items-center text-[14px]">
                      <span class="text-[#86868b]">Imposto de Renda ({{ investment.result.taxRate }}%)</span>
                      <span class="font-medium text-[#FF3B30]">- {{ formatBRL(investment.result.taxAmount) }}</span>
                    </div>
                    <div
                      class="pt-3 mt-3 border-t border-black/5 dark:border-white/10 flex justify-between items-center">
                      <span class="font-bold text-[#1d1d1f] dark:text-white">Valor Líquido</span>
                      <span class="font-bold text-[20px] text-[#7B2CBF]">{{ formatBRL(investment.result.netValue)
                        }}</span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- TABELA DE AMORTIZAÇÃO -->
              <div v-else-if="activeFinancialCalc === 'amortization'">
                <h2 class="text-[22px] font-bold mb-6 text-[#1d1d1f] dark:text-white">Tabela de Amortização</h2>
                <div class="grid gap-5">
                  <div class="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div class="input-group">
                      <label>Valor Financiado</label>
                      <input v-model.number="amortization.amount" type="number" placeholder="100000" />
                    </div>
                    <div class="input-group">
                      <label>Taxa Anual (%)</label>
                      <input v-model.number="amortization.rate" type="number" placeholder="9" />
                    </div>
                  </div>
                  <div class="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div class="input-group">
                      <label>Parcelas</label>
                      <input v-model.number="amortization.periods" type="number" placeholder="360" />
                    </div>
                    <div class="input-group">
                      <label>Sistema</label>
                      <div class="relative">
                        <select v-model="amortization.type"
                          class="appearance-none w-full bg-[#F5F5F7] dark:bg-[#2c2c2e] border border-transparent focus:border-[#7B2CBF] rounded-[10px] px-4 py-3 text-[15px] outline-none transition-all">
                          <option value="PRICE">PRICE (Parcelas Fixas)</option>
                          <option value="SAC">SAC (Parcelas Decrescentes)</option>
                        </select>
                        <span
                          class="absolute right-4 top-1/2 -translate-y-1/2 material-symbols-outlined text-[18px] text-[#86868b] pointer-events-none">expand_more</span>
                      </div>
                    </div>
                  </div>
                  <button @click="calculateAmortization" class="btn-action mt-2">Gerar Tabela</button>

                  <div v-if="amortization.result"
                    class="mt-6 border border-black/5 dark:border-white/10 rounded-[14px] overflow-hidden">
                    <div class="max-h-[400px] overflow-y-auto custom-scroll">
                      <table class="w-full text-left text-[13px]">
                        <thead class="bg-[#f5f5f7] dark:bg-[#2c2c2e] text-[#86868b] sticky top-0 z-10">
                          <tr>
                            <th class="px-4 py-3 font-semibold">Mês</th>
                            <th class="px-4 py-3 font-semibold">Parcela</th>
                            <th class="px-4 py-3 font-semibold">Juros</th>
                            <th class="px-4 py-3 font-semibold">Saldo</th>
                          </tr>
                        </thead>
                        <tbody class="divide-y divide-black/5 dark:divide-white/5">
                          <tr v-for="row in amortization.result.table" :key="row.period"
                            class="bg-white dark:bg-[#1c1c1e]">
                            <td class="px-4 py-2.5 text-[#86868b]">{{ row.period }}</td>
                            <td class="px-4 py-2.5 font-medium text-[#1d1d1f] dark:text-white">{{ formatBRL(row.payment)
                              }}</td>
                            <td class="px-4 py-2.5 text-[#FF3B30]">{{ formatBRL(row.interest) }}</td>
                            <td class="px-4 py-2.5 text-[#86868b]">{{ formatBRL(row.balance) }}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>

              <!-- EVOLUÇÃO DE DÍVIDA -->
              <div v-else-if="activeFinancialCalc === 'debtSimulator'">
                <h2 class="text-[22px] font-bold mb-6 text-[#1d1d1f] dark:text-white">Evolução de Dívida</h2>
                <div class="grid gap-5">
                  <div class="input-group">
                    <label>Valor Original (R$)</label>
                    <input v-model.number="debt.amount" type="number" placeholder="1000" />
                  </div>
                  <div class="grid grid-cols-2 gap-5">
                    <div class="input-group">
                      <label>Juros Mensal (%)</label>
                      <input v-model.number="debt.rate" type="number" placeholder="14" />
                    </div>
                    <div class="input-group">
                      <label>Meses em Atraso</label>
                      <input v-model.number="debt.months" type="number" placeholder="12" />
                    </div>
                  </div>
                  <button @click="calculateDebtGrowth"
                    class="btn-action mt-2 bg-[#FF3B30] hover:bg-[#D32F2F] shadow-[#FF3B30]/30">Calcular Dívida</button>

                  <div v-if="debt.result" class="result-card mt-6 p-6 text-center border-l-4 border-l-[#FF3B30]">
                    <p class="text-[13px] uppercase tracking-wide font-semibold text-[#86868b] mb-2">Valor Atualizado
                    </p>
                    <p class="text-[36px] font-bold text-[#FF3B30]">{{ formatBRL(debt.result.totalOwed) }}</p>
                    <p class="text-[13px] text-[#86868b] mt-1">Sendo {{ formatBRL(debt.result.totalInterest) }} só de
                      juros</p>
                  </div>
                </div>
              </div>

              <!-- ORÇAMENTO -->
              <div v-else-if="activeFinancialCalc === 'budget'">
                <h2 class="text-[22px] font-bold mb-6 text-[#1d1d1f] dark:text-white">Orçamento Mensal</h2>
                <div class="grid gap-5">
                  <div class="input-group">
                    <label>Renda Líquida (R$)</label>
                    <input v-model.number="budget.totalIncome" type="number" placeholder="5000" />
                  </div>

                  <div class="space-y-3">
                    <label class="block text-[12px] font-bold uppercase tracking-wider text-[#86868b] ml-1">Categorias
                      de Gastos</label>
                    <div v-for="(category, index) in budget.categories" :key="index" class="flex gap-3">
                      <input type="text" v-model="category.name"
                        class="flex-1 h-11 px-4 bg-[#F5F5F7] dark:bg-[#2c2c2e] rounded-[10px] text-[15px] outline-none"
                        placeholder="Nome" />
                      <input type="number" v-model.number="category.value"
                        class="w-32 h-11 px-4 bg-[#F5F5F7] dark:bg-[#2c2c2e] rounded-[10px] text-[15px] outline-none"
                        placeholder="Valor" />
                    </div>
                  </div>

                  <div class="flex gap-3 mt-2">
                    <button @click="addBudgetCategory"
                      class="flex-1 py-3 rounded-[12px] border border-black/10 dark:border:white/10 font-semibold text-[#1d1d1f] dark:text:white hover:bg-[#F5F5F7] dark:hover:bg-[#2c2c2e] transition-colors flex items-center justify-center gap-2">
                      <span class="material-symbols-outlined text-[18px]">add</span> Item
                    </button>
                    <button @click="calculateBudget" class="flex-1 btn-action m-0">Analisar</button>
                  </div>

                  <div v-if="budget.result" class="result-card mt-6">
                    <div
                      class="grid grid-cols-2 divide-x divide-black/5 dark:divide-white/10 border-b border-black/5 dark:border-white/10">
                      <div class="p-4 text-center">
                        <p class="text-[12px] text-[#86868b] mb-1">Total Gastos</p>
                        <p class="font-semibold text-[#FF3B30]">{{ formatBRL(budget.result.totalExpenses) }}</p>
                      </div>
                      <div class="p-4 text-center">
                        <p class="text-[12px] text-[#86868b] mb-1">Saldo Final</p>
                        <p class="font-semibold"
                          :class="budget.result.balance >= 0 ? 'text-[#34C759]' : 'text-[#FF3B30]'">{{
                            formatBRL(budget.result.balance) }}</p>
                      </div>
                    </div>
                    <div class="p-4 space-y-3">
                      <div v-for="cat in budget.result.categories" :key="cat.name"
                        class="flex items-center justify-between text-[14px]">
                        <div class="flex items-center gap-2">
                          <span class="w-2 h-2 rounded-full bg-[#7B2CBF]"></span>
                          <span class="text-[#1d1d1f] dark:text-white">{{ cat.name }}</span>
                        </div>
                        <span class="text-[#86868b]">{{ formatBRL(cat.value) }} ({{ cat.percentage }}%)</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </transition>
        </main>

      </div>
    </transition>
  </div>
</template>

<script setup>
import { ref } from 'vue'

const currentMode = ref('financial')
const activeFinancialCalc = ref('compoundInterest')

const financialCalculators = [
  { id: 'compoundInterest', name: 'Juros Compostos', icon: 'trending_up' },
  { id: 'loanPayment', name: 'Financiamento', icon: 'account_balance' },
  { id: 'inflation', name: 'Inflação', icon: 'trending_down' },
  { id: 'retirement', name: 'Aposentadoria', icon: 'elderly' },
  { id: 'investmentYield', name: 'Rendimento', icon: 'query_stats' },
  { id: 'amortization', name: 'Amortização', icon: 'table_view' },
  { id: 'debtSimulator', name: 'Dívidas', icon: 'warning' },
  { id: 'budget', name: 'Orçamento', icon: 'pie_chart' }
]

const displayValue = ref('0')
const firstOperand = ref(null)
const operator = ref(null)
const waitingForSecondOperand = ref(false)
const expressionDisplay = ref('')

const inputDigit = (digit) => {
  if (waitingForSecondOperand.value) {
    displayValue.value = digit
    waitingForSecondOperand.value = false
  } else {
    displayValue.value = displayValue.value === '0' ? digit : displayValue.value + digit
  }
}

const inputDecimal = () => {
  if (waitingForSecondOperand.value) {
    displayValue.value = '0.'
    waitingForSecondOperand.value = false
    return
  }
  if (!displayValue.value.includes('.')) displayValue.value += '.'
}

const handleOperator = (nextOperator) => {
  const inputValue = parseFloat(displayValue.value)
  if (operator.value && waitingForSecondOperand.value) {
    operator.value = nextOperator
    expressionDisplay.value = `${firstOperand.value} ${nextOperator}`
    return
  }
  if (firstOperand.value === null) {
    firstOperand.value = inputValue
  } else if (operator.value) {
    const result = performCalculation[operator.value](firstOperand.value, inputValue)
    displayValue.value = String(result)
    firstOperand.value = result
  }
  waitingForSecondOperand.value = true
  operator.value = nextOperator
  expressionDisplay.value = `${firstOperand.value} ${nextOperator}`
}

const performCalculation = {
  '/': (a, b) => a / b,
  '*': (a, b) => a * b,
  '+': (a, b) => a + b,
  '-': (a, b) => a - b,
  '=': (a, b) => b
}

const calculate = () => {
  if (!operator.value || firstOperand.value === null) return
  const secondOperand = parseFloat(displayValue.value)
  const result = performCalculation[operator.value](firstOperand.value, secondOperand)
  expressionDisplay.value = `${firstOperand.value} ${operator.value} ${secondOperand} =`
  displayValue.value = String(result)
  firstOperand.value = null
  operator.value = null
  waitingForSecondOperand.value = false
}

const clear = () => {
  displayValue.value = '0'
  firstOperand.value = null
  operator.value = null
  waitingForSecondOperand.value = false
  expressionDisplay.value = ''
}

const toggleSign = () => { displayValue.value = String(parseFloat(displayValue.value) * -1) }

const handlePercentage = () => {
  if (firstOperand.value === null) {
    displayValue.value = String(parseFloat(displayValue.value) / 100)
    return
  }
  const secondOperand = parseFloat(displayValue.value)
  let result
  if (operator.value === '+' || operator.value === '-') {
    const percentageValue = (firstOperand.value * secondOperand) / 100
    result = performCalculation[operator.value](firstOperand.value, percentageValue)
  } else if (operator.value === '*' || operator.value === '/') {
    result = performCalculation[operator.value](firstOperand.value, secondOperand / 100)
  }
  if (result !== undefined) {
    displayValue.value = String(result)
    expressionDisplay.value = `${expressionDisplay.value} =`
    firstOperand.value = null
    operator.value = null
    waitingForSecondOperand.value = false
  }
}

const ci = ref({ initial: null, monthly: null, rate: null, years: null, result: null })
const calculateCompoundInterest = () => {
  const P = ci.value.initial || 0
  const PMT = ci.value.monthly || 0
  const r = (ci.value.rate || 0) / 100 / 12
  const n = (ci.value.years || 0) * 12
  let futureValue
  if (r === 0) futureValue = P + PMT * n
  else futureValue = P * Math.pow(1 + r, n) + PMT * ((Math.pow(1 + r, n) - 1) / r) * (1 + r)
  const totalInvested = P + PMT * n
  const totalInterest = futureValue - totalInvested
  ci.value.result = { futureValue, totalInvested, totalInterest }
}

const loan = ref({ amount: null, rate: null, years: null, result: null })
const calculateLoanPayment = () => {
  const P = loan.value.amount || 0
  const r = (loan.value.rate || 0) / 100 / 12
  const n = (loan.value.years || 0) * 12
  let monthlyPayment = 0
  if (n > 0) monthlyPayment = r === 0 ? P / n : P * (r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1)
  const totalCost = monthlyPayment * n
  const totalInterest = totalCost - P
  loan.value.result = { monthlyPayment, totalCost, totalInterest }
}

const inflation = ref({ currentValue: null, rate: null, years: null, result: null })
const calculateInflation = () => {
  const P = inflation.value.currentValue || 0
  const i = (inflation.value.rate || 0) / 100
  const n = inflation.value.years || 0
  const futureValue = P / Math.pow(1 + i, n)
  inflation.value.result = { futureValue }
}

const retirement = ref({ currentAge: 30, retirementAge: 65, monthlyIncome: 5000, returnRate: 8, result: null })
const calculateRetirement = () => {
  const yearsToInvest = (retirement.value.retirementAge || 0) - (retirement.value.currentAge || 0)
  const monthsToInvest = yearsToInvest * 12
  const monthsInRetirement = (85 - (retirement.value.retirementAge || 0)) * 12
  const preRate = (retirement.value.returnRate || 0) / 100 / 12
  const postRate = 4 / 100 / 12
  const pmt = retirement.value.monthlyIncome || 0
  const totalNeeded = pmt * ((Math.pow(1 + postRate, monthsInRetirement) - 1) / postRate) / Math.pow(1 + postRate, monthsInRetirement)
  let monthlyInvestment = 0
  if (preRate > 0 && monthsToInvest > 0) {
    monthlyInvestment = totalNeeded * preRate / (Math.pow(1 + preRate, monthsToInvest) - 1) / (1 + preRate)
  }
  retirement.value.result = { totalNeeded, monthlyInvestment }
}

const budget = ref({ totalIncome: null, categories: [{ name: 'Moradia', value: null }, { name: 'Transporte', value: null }], result: null })
const addBudgetCategory = () => { budget.value.categories.push({ name: '', value: null }) }
const calculateBudget = () => {
  const totalIncome = budget.value.totalIncome || 0
  const totalExpenses = budget.value.categories.reduce((acc, c) => acc + (c.value || 0), 0)
  const balance = totalIncome - totalExpenses
  const categories = budget.value.categories.map(c => ({ ...c, percentage: totalIncome > 0 ? (((c.value || 0) / totalIncome) * 100).toFixed(1) : 0 }))
  budget.value.result = { totalExpenses, balance, categories }
}

const amortization = ref({ type: 'PRICE', amount: 100000, rate: 9, periods: 360, result: null })
const calculateAmortization = () => {
  const P = amortization.value.amount || 0
  const n = amortization.value.periods || 0
  const annual = (amortization.value.rate || 0) / 100
  const r = Math.pow(1 + annual, 1 / 12) - 1
  let balance = P
  const table = []
  if (n <= 0 || P <= 0) { amortization.value.result = { table: [] }; return }
  if (amortization.value.type === 'PRICE') {
    const pmt = P * (r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1)
    for (let i = 1; i <= n; i++) {
      const interest = balance * r
      const principal = pmt - interest
      balance -= principal
      table.push({ period: i, payment: pmt, interest, principal, balance: balance > 0 ? balance : 0 })
    }
  } else {
    const principal = P / n
    for (let i = 1; i <= n; i++) {
      const interest = balance * r
      const payment = principal + interest
      balance -= principal
      table.push({ period: i, payment, interest, principal, balance: balance > 0 ? balance : 0 })
    }
  }
  amortization.value.result = { table }
}

const investment = ref({ initial: 10000, rate: 12, days: 730, result: null })
const calculateInvestmentYield = () => {
  const P = investment.value.initial || 0
  const annual = (investment.value.rate || 0) / 100
  const n = investment.value.days || 0
  const grossValue = P * Math.pow(1 + annual, n / 365)
  const grossProfit = grossValue - P
  let taxRateValue = 0
  if (n <= 180) taxRateValue = 22.5
  else if (n <= 360) taxRateValue = 20
  else if (n <= 720) taxRateValue = 17.5
  else taxRateValue = 15
  const taxAmount = grossProfit * (taxRateValue / 100)
  const netValue = P + grossProfit - taxAmount
  investment.value.result = { grossValue, grossProfit, taxRate: taxRateValue, taxAmount, netValue }
}

const debt = ref({ amount: 1000, rate: 14, months: 12, result: null })
const calculateDebtGrowth = () => {
  const P = debt.value.amount || 0
  const r = (debt.value.rate || 0) / 100
  const n = debt.value.months || 0
  const totalOwed = P * Math.pow(1 + r, n)
  const totalInterest = totalOwed - P
  debt.value.result = { totalOwed, totalInterest }
}

const formatBRL = (v) => {
  if (typeof v !== 'number' || isNaN(v)) return 'R$ 0,00'
  return v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
}
</script>

<style scoped>
.font-sans {
  font-family: -apple-system, BlinkMacSystemFont, "SF Pro Text", "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
}

.input-group label {
  @apply block text-[12px] font-bold uppercase tracking-wider text-[#86868b] mb-1.5 ml-1;
}

.input-group input {
  @apply w-full h-[46px] px-4 bg-[#F5F5F7] dark:bg-[#2c2c2e] rounded-[10px] text-[15px] text-[#1d1d1f] dark:text-white placeholder-[#86868b] border border-transparent focus:border-[#7B2CBF] focus:bg-white dark:focus:bg-black outline-none transition-all;
}

.btn-action {
  @apply w-full py-3.5 rounded-[12px] bg-[#7B2CBF] hover:bg-[#6823a3] text-white font-semibold text-[15px] shadow-lg shadow-purple-500/20 active:scale-[0.98] transition-all mt-4;
}

.result-card {
  @apply bg-white dark:bg-[#2c2c2e]/50 border border-black/5 dark:border-white/10 rounded-[16px] overflow-hidden;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.fade-fast-enter-active,
.fade-fast-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.fade-fast-enter-from,
.fade-fast-leave-to {
  opacity: 0;
  transform: translateY(10px);
}

.custom-scroll::-webkit-scrollbar {
  width: 4px;
  height: 4px;
}

.custom-scroll::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.1);
  border-radius: 10px;
}

.dark .custom-scroll::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.1);
}

.custom-scroll::-webkit-scrollbar-track {
  background: transparent;
}
</style>